import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Clock } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PostCard } from "@/components/post-card";
import { RunStatusBadge } from "@/components/run-status-badge";
import { SampleDonut } from "@/components/sample-donut";
import { api } from "@/lib/api";
import {
  formatNumber,
  formatNumberFull,
  formatPercent,
  formatReportDate,
  formatPercentage,
  formatTimestamp,
} from "@/lib/format";
import type { ClassifiedPost } from "@/lib/types";

export function RunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const runId = id ?? "";

  const detailQuery = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api.run(runId),
    enabled: Boolean(runId),
  });

  if (!runId) {
    return <div>Missing run id</div>;
  }

  if (detailQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="flex items-center gap-3 p-5 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              Could not load run #{runId}.{" "}
              {detailQuery.error instanceof Error
                ? detailQuery.error.message
                : "Unknown error"}
            </span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (detailQuery.isLoading || !detailQuery.data) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr]">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const { run, posts } = detailQuery.data;
  const antisemiticPosts = posts.filter((p) => p.label === "antisemitic");
  const notAntisemiticPosts = posts.filter(
    (p) => p.label === "not_antisemitic"
  );
  const unclassifiedPosts = posts.filter((p) => p.label == null);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <BackLink />
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <RunStatusBadge status={run.status} />
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {run.report_timezone}
              </span>
              <span>Run #{run.id}</span>
            </div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight">
              {formatReportDate(run.report_date)}
            </h1>
            <p className="text-sm text-muted-foreground">
              UTC window {formatTimestamp(run.utc_window_start)} →{" "}
              {formatTimestamp(run.utc_window_end)}
            </p>
          </div>
          <div className="flex flex-col items-start md:items-end">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Daily percentage
            </span>
            <div className="text-5xl font-semibold tabular-nums tracking-tight">
              {formatPercentage(run.daily_score)}
            </div>
            <span className="text-xs text-muted-foreground">
              {formatPercent(run.antisemitic_sample_rate, 2)} of analyzed
              sample
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.6fr_1fr]">
        <RunMetaCard
          run={run}
          modelName={posts.find((p) => p.model_name)?.model_name ?? null}
          promptVersion={
            posts.find((p) => p.prompt_version)?.prompt_version ?? null
          }
        />
        <SampleDonut
          antisemitic={antisemiticPosts.length}
          notAntisemitic={notAntisemiticPosts.length}
          unclassified={unclassifiedPosts.length}
        />
      </div>

      <PostsTabs
        all={posts}
        antisemitic={antisemiticPosts}
        notAntisemitic={notAntisemiticPosts}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Button asChild variant="ghost" size="sm" className="-ml-2 self-start">
      <Link to="/" className="text-muted-foreground hover:text-foreground">
        <ArrowLeft className="mr-1 h-3.5 w-3.5" />
        All runs
      </Link>
    </Button>
  );
}

function RunMetaCard({
  run,
  modelName,
  promptVersion,
}: {
  run: import("@/lib/types").DailyRun;
  modelName: string | null;
  promptVersion: string | null;
}) {
  const items = useMemo(
    () => [
      {
        label: "Antisemitic posts / sample size",
        value: `${formatNumberFull(run.antisemitic_sample_count)} / ${formatNumberFull(run.sample_size_analyzed)}`,
      },
      {
        label: "Sample analyzed / requested",
        value: `${formatNumberFull(run.sample_size_analyzed)} / ${formatNumberFull(run.sample_size_requested)}`,
      },
      {
        label: "% antisemitic posts in sample",
        value: formatPercent(run.antisemitic_sample_rate, 2),
      },
      {
        label: "Total matching posts (window)",
        value: formatNumber(run.total_matching_post_count),
      },
      {
        label: "Estimated antisemitic posts",
        value: formatNumber(run.estimated_antisemitic_post_count),
      },
      {
        label: "Model",
        value: modelName ?? "—",
      },
      {
        label: "Prompt version",
        value: promptVersion ?? "—",
      },
    ],
    [run, modelName, promptVersion]
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Run details</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-2">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          {items.map((item) => (
            <div key={item.label} className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {item.label}
              </span>
              <span className="text-sm font-medium tabular-nums">
                {item.value}
              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-1.5 border-t border-border/40 pt-3">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            X query
          </span>
          <code className="rounded-md border border-border/40 bg-secondary/30 px-3 py-2 font-mono text-xs leading-relaxed text-foreground/80">
            {run.query_text}
          </code>
        </div>
      </CardContent>
    </Card>
  );
}

function PostsTabs({
  all,
  antisemitic,
  notAntisemitic,
}: {
  all: ClassifiedPost[];
  antisemitic: ClassifiedPost[];
  notAntisemitic: ClassifiedPost[];
}) {
  if (all.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          This run has no sampled posts in the database.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h2 className="text-base font-semibold tracking-tight">
          Sampled posts
        </h2>
        <p className="text-xs text-muted-foreground">
          Sorted with the most-confident antisemitic classifications first.
        </p>
      </div>
      <Tabs defaultValue="antisemitic" className="w-full">
        <TabsList>
          <TabsTrigger value="antisemitic">
            Antisemitic ({antisemitic.length})
          </TabsTrigger>
          <TabsTrigger value="not_antisemitic">
            Not antisemitic ({notAntisemitic.length})
          </TabsTrigger>
          <TabsTrigger value="all">All ({all.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="antisemitic">
          <PostList
            posts={antisemitic}
            empty="No posts in this sample were classified as antisemitic."
          />
        </TabsContent>
        <TabsContent value="not_antisemitic">
          <PostList
            posts={notAntisemitic}
            empty="No non-antisemitic posts to show."
          />
        </TabsContent>
        <TabsContent value="all">
          <PostList posts={all} empty="No posts to show." />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PostList({
  posts,
  empty,
}: {
  posts: ClassifiedPost[];
  empty: string;
}) {
  if (posts.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {empty}
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {posts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}
    </div>
  );
}
